import type { ColumnResizedEvent, GridApi } from "ag-grid-enterprise";

// Tracks which columns a user has explicitly resized and their widths — the source of truth for
// what autosize should skip and what the server needs to persist. Widths are keyed per scope
// (e.g. a question id) except declared-shared colIds, which use a bare key; no scope set means everything is shared.
export class UserSizedColumns {
  private widths = new Map<string, number>();
  private scope = "";
  private sharedColIds = new Set<string>();

  setScope(scopeId: string | number | null | undefined): void {
    this.scope = scopeId === null || scopeId === undefined ? "" : String(scopeId);
  }

  setSharedColIds(colIds: string[]): void {
    this.sharedColIds = new Set(colIds);
  }

  private keyFor(colId: string): string {
    if (!this.scope || this.sharedColIds.has(colId)) return colId;
    return `${this.scope}:${colId}`;
  }

  // Scope ids and colIds never contain ":", so splitting on the first one is unambiguous.
  private parseKey(key: string): { scope: string; colId: string } {
    const idx = key.indexOf(":");
    return idx === -1 ? { scope: "", colId: key } : { scope: key.slice(0, idx), colId: key.slice(idx + 1) };
  }

  // A double-clicked resize handle also reports "uiColumnResized" — tracked on purpose.
  // A sibling squeezed by flex is never user intent.
  recordResize(event: ColumnResizedEvent): void {
    if (!event.finished || event.source !== "uiColumnResized") return;
    const flexed = new Set(event.flexColumns ?? []);
    for (const column of event.columns ?? []) {
      if (flexed.has(column)) continue;
      this.widths.set(this.keyFor(column.getColId()), column.getActualWidth());
    }
  }

  // Merge, never replace: an in-session drag beats a restored entry. Malformed input is read
  // as "nothing is user-sized", never thrown.
  seedFrom(persistedState: Record<string, unknown>): void {
    const raw = persistedState.userSizedWidths;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return;
    for (const [key, width] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof width !== "number") continue;
      if (!this.widths.has(key)) this.widths.set(key, width);
    }
  }

  // Whole flat map, every scope — never pruned; another scope's widths must survive this one.
  toPersistShape(): Record<string, number> {
    return Object.fromEntries(this.widths.entries());
  }

  colIds(): string[] {
    const result = new Set<string>();
    for (const key of this.widths.keys()) {
      const { scope, colId } = this.parseKey(key);
      if (scope === "" || scope === this.scope) result.add(colId);
    }
    return [...result];
  }

  applyTo(api: GridApi, knownColIds: ReadonlySet<string>): void {
    if (this.widths.size === 0) return;
    const state = [...this.widths.entries()]
      .map(([key, width]) => ({ ...this.parseKey(key), width }))
      .filter(({ scope, colId }) => (scope === "" || scope === this.scope) && knownColIds.has(colId))
      .map(({ colId, width }) => ({ colId, width, flex: null }));
    if (state.length === 0) return;
    api.applyColumnState({ state });
  }

  // Reset Columns acts on the grid in front of the user: this scope plus the shared columns it
  // displays, never another scope's widths.
  clear(): void {
    for (const key of [...this.widths.keys()]) {
      const { scope } = this.parseKey(key);
      if (scope === "" || scope === this.scope) this.widths.delete(key);
    }
  }

  isEmpty(): boolean {
    return this.widths.size === 0;
  }
}
